import { useEffect, useRef } from 'react';
import { useBoard, usePane, usePaneStore, useYMap } from '../context';
import type { BoardMetaSettings } from '../BoardSettings';
import { ItemView } from '../items/ItemView';
import { SelectionOverlay } from '../selection/SelectionOverlay';
import { lazy, Suspense } from 'react';
import { RICH_TYPES } from '../../doc/schema';
const FormatBar = lazy(() => import('../text/FormatBar').then((m) => ({ default: m.FormatBar })));
import { useToolSettings } from '../toolSettings';
import { DEG, type Point, type Size } from '../../doc/geometry';
import { screenBox, screenToWorld, worldToScreen, type Camera } from './camera';
import { CutoutOverlay } from './CutoutOverlay';
import { DrawOverlay } from './DrawOverlay';
import { useGestures } from './useGestures';
import { useVisibleItemIds } from './useVisibleItems';


/** An eyedropper whose tip (bottom left) is the hot spot; outlined so it shows on any canvas. */
const PICKER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="-1 -1 18 18" fill="none" stroke-linecap="square">' +
    '<g stroke="#fff" stroke-width="4"><path d="M9 6l-6 6-1 2 2-1 6-6M7.5 4.5l4 4M9.5 4.5L12 2a1.4 1.4 0 0 1 2 2l-2.5 2.5z"/></g>' +
    '<g stroke="#111" stroke-width="1.5"><path d="M9 6l-6 6-1 2 2-1 6-6M7.5 4.5l4 4"/><path d="M9.5 4.5L12 2a1.4 1.4 0 0 1 2 2l-2.5 2.5z" fill="#111"/></g>' +
    '</svg>',
)}") 2 18, crosshair`;

/** Spare grid around the viewport: one cell at its largest (96px) and the rounding to whole cells. */
const GRID_MARGIN = 128;

/** The pannable/zoomable surface. Camera changes are applied straight to the DOM, outside React. */
export function Viewport() {
  const ref = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const pane = usePaneStore();
  const board = useBoard();
  const meta = useYMap<unknown>(board.meta) as BoardMetaSettings;
  const gridStyle = meta.grid ?? 'dots';
  const gridWorld = meta.gridSize ?? 32;
  const bg = meta.bg ?? null;
  const handlers = useGestures(ref);
  const ids = useVisibleItemIds();
  const tool = usePane((s) => s.tool);
  const spaceHeld = usePane((s) => s.spaceHeld);
  const dragOver = usePane((s) => s.dragOver);
  const penMode = useToolSettings((s) => s.penMode);
  const editingRich = usePane((s) => {
    const it = s.editingId ? board.index.get(s.editingId) : undefined;
    return !!it && RICH_TYPES.has(it.type);
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => pane.getState().setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pane]);

  useEffect(() => {
    const el = ref.current;
    const world = worldRef.current;
    const grid = gridRef.current;
    if (!el || !world || !grid) return;
    el.style.backgroundColor = bg ?? '';
    grid.style.backgroundImage =
      gridStyle === 'lines'
        ? 'linear-gradient(var(--canvas-dot) 1px, transparent 1px), linear-gradient(90deg, var(--canvas-dot) 1px, transparent 1px)'
        : gridStyle === 'none'
          ? 'none'
          : 'radial-gradient(var(--canvas-dot) 1px, transparent 1.5px)';
    let lastStep = 0;
    let lastW = 0;
    let lastH = 0;
    const apply = (c: Camera, size: Size) => {
      world.style.transform = `translate(${c.x}px, ${c.y}px) rotate(${c.rot}deg) scale(${c.zoom})`;
      if (gridStyle === 'none') return;
      // keep the grid readable: double the spacing until cells are at least 16px, halve above 96px
      let step = gridWorld * c.zoom;
      while (step < 16) step *= 2;
      while (step > 96) step /= 2;
      if (step !== lastStep) {
        grid.style.backgroundSize = `${step}px ${step}px`;
        lastStep = step;
      }
      // The grid layer is a composited element larger than the viewport; sliding it by the
      // camera offset modulo one cell is a transform-only change (no repaint each frame),
      // unlike animating background-position on the viewport itself.
      // The layer turns with the canvas about its own top left corner, so it has to cover the
      // viewport as seen in the grid's frame (its rotated bounds) plus a cell on every side.
      const cos = Math.cos(c.rot * DEG);
      const sin = Math.sin(c.rot * DEG);
      const w = Math.ceil(size.w * Math.abs(cos) + size.h * Math.abs(sin)) + GRID_MARGIN * 2;
      const h = Math.ceil(size.w * Math.abs(sin) + size.h * Math.abs(cos)) + GRID_MARGIN * 2;
      if (w !== lastW || h !== lastH) {
        grid.style.width = `${w}px`;
        grid.style.height = `${h}px`;
        lastW = w;
        lastH = h;
      }
      // the viewport centre in the grid's frame, measured from the world origin
      const dx = size.w / 2 - c.x;
      const dy = size.h / 2 - c.y;
      const ux = dx * cos + dy * sin;
      const uy = dy * cos - dx * sin;
      // start the layer a whole number of cells from the world origin, centred on the viewport
      const kx = Math.round((ux - w / 2) / step) * step;
      const ky = Math.round((uy - h / 2) / step) * step;
      const ox = c.x + kx * cos - ky * sin;
      const oy = c.y + kx * sin + ky * cos;
      grid.style.transform = `translate(${ox}px, ${oy}px) rotate(${c.rot}deg)`;
    };
    const st = pane.getState();
    apply(st.camera, st.size);
    return pane.subscribe((s, prev) => {
      if (s.camera !== prev.camera || s.size !== prev.size) apply(s.camera, s.size);
    });
  }, [pane, bg, gridStyle, gridWorld]);

  const cursor =
    spaceHeld || tool === 'pan'
      ? 'grab'
      : tool === 'select'
        ? 'default'
        : tool === 'draw' && penMode === 'eraser'
          ? 'cell'
          : tool === 'draw' && penMode === 'picker'
            ? PICKER_CURSOR
            : 'crosshair';

  return (
    <div
      ref={ref}
      className={`viewport tool-${tool}${tool === 'draw' ? ` mode-${penMode}` : ''}${dragOver ? ' is-dragover' : ''}`}
      tabIndex={0}
      style={{ cursor }}
      {...handlers}
    >
      <div ref={gridRef} className="grid-layer" aria-hidden />
      <div ref={worldRef} className="world">
        {ids.map((id) => (
          <ItemView key={id} id={id} />
        ))}
      </div>
      <MarqueeOverlay />
      <CreateOverlay />
      <SnapGuides />
      <SelectionOverlay />
      {editingRich && (
        <Suspense fallback={null}>
          <FormatBar />
        </Suspense>
      )}
      <DrawOverlay />
      <CutoutOverlay />
    </div>
  );
}

function MarqueeOverlay() {
  const marquee = usePane((s) => s.marquee);
  const camera = usePane((s) => s.camera);
  if (!marquee) return null;
  return (
    <div
      className="marquee"
      style={{ ...screenBox(camera, marquee), transform: `rotate(${camera.rot}deg)` }}
    />
  );
}

function SnapGuides() {
  const g = usePane((s) => s.snapGuides);
  const camera = usePane((s) => s.camera);
  const size = usePane((s) => s.size);
  if (g.x === null && g.y === null) return null;
  if (!camera.rot) {
    return (
      <>
        {g.x !== null && <div className="snap-guide snap-guide-v" style={{ left: g.x * camera.zoom + camera.x }} />}
        {g.y !== null && <div className="snap-guide snap-guide-h" style={{ top: g.y * camera.zoom + camera.y }} />}
      </>
    );
  }
  // A rotated canvas: each guide is a line long enough to cross the viewport at any angle,
  // centred on the point of the guide nearest the middle of the view.
  const len = (size.w + size.h) * 2;
  const mid = screenToWorld(camera, { x: size.w / 2, y: size.h / 2 });
  const line = (world: Point, vertical: boolean) => {
    const p = worldToScreen(camera, world);
    return {
      left: vertical ? p.x : p.x - len / 2,
      top: vertical ? p.y - len / 2 : p.y,
      right: 'auto',
      bottom: 'auto',
      ...(vertical ? { height: len } : { width: len }),
      transform: `rotate(${camera.rot}deg)`,
    };
  };
  return (
    <>
      {g.x !== null && <div className="snap-guide snap-guide-v" style={line({ x: g.x, y: mid.y }, true)} />}
      {g.y !== null && <div className="snap-guide snap-guide-h" style={line({ x: mid.x, y: g.y }, false)} />}
    </>
  );
}

function CreateOverlay() {
  const rect = usePane((s) => s.creating);
  const camera = usePane((s) => s.camera);
  if (!rect) return null;
  return (
    <div
      className="create-preview"
      style={{ ...screenBox(camera, rect), transform: `rotate(${camera.rot}deg)` }}
    />
  );
}
